/* eslint-disable max-lines */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import toast from 'react-hot-toast';
import {
  Package,
  Calendar,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  X,
  Infinity,
  ChevronDown
} from 'lucide-react';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { StatusBadge, Badge } from '@components/atoms/ui-kit/badge';
import { useGetAdminOrganizationSubscription } from '@utils/api/admin/use-get-admin-organization-subscription';
import { useUpdateAdminOrganizationSubscription } from '@utils/api/admin/use-update-admin-organization-subscription';
import { useResetAdminOrganizationSubscription } from '@utils/api/admin/use-reset-admin-organization-subscription';
import { useGetAdminPlans } from '@utils/api/admin/use-get-admin-plans';
import { AdminPlan } from '@utils/types/admin';

type Props = {
  organizationId: string;
};

export default function OrganizationSubscriptionTab({ organizationId }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    isError: subscriptionError
  } = useGetAdminOrganizationSubscription(organizationId);
  const { data: plansData, isLoading: plansLoading } = useGetAdminPlans();
  const updateMutation = useUpdateAdminOrganizationSubscription();
  const resetMutation = useResetAdminOrganizationSubscription();

  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const subscription = subscriptionData?.result;
  const plans = plansData?.result ?? [];

  // Find the current plan from plans list to get additional details
  const currentPlan = plans.find(p => p.id === subscription?.plan_id);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUsagePercentage = (used: number, limit: number | null): number => {
    if (limit === null || limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageClass = (percentage: number): string => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const handleChangePlan = () => {
    if (selectedPlanId && subscription) {
      updateMutation.mutate(
        {
          organizationId,
          payload: { plan_id: selectedPlanId }
        },
        {
          onSuccess: () => {
            setChangePlanOpen(false);
            setSelectedPlanId(null);
          },
          onError: (error: unknown) => {
            const err = error as {
              response?: {
                data?: {
                  error_description?: string;
                  detail?: string;
                  message?: string;
                };
              };
            };
            const msg =
              err?.response?.data?.error_description ||
              err?.response?.data?.detail ||
              err?.response?.data?.message ||
              t('an-error-occurred');
            toast.error(msg);
          }
        }
      );
    }
  };

  const handleResetUsage = () => {
    resetMutation.mutate(organizationId, {
      onSuccess: () => {
        setResetConfirmOpen(false);
      },
      onError: (error: unknown) => {
        const err = error as {
          response?: {
            data?: {
              error_description?: string;
              detail?: string;
              message?: string;
            };
          };
        };
        const msg =
          err?.response?.data?.error_description ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          t('an-error-occurred');
        toast.error(msg);
      }
    });
  };

  if (subscriptionLoading || plansLoading) {
    return <Skeleton variant="table" rows={5} className="mt-2" />;
  }

  if (subscriptionError) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-red-500 dark:text-red-400">
          {t('an-error-occurred')}
        </p>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="py-8 text-center">
        <Package
          className="h-12 w-12 mx-auto mb-4"
          style={{ color: getColour('inactiveText') }}
        />
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('no-subscription-data')}
        </p>
      </div>
    );
  }

  // Get limit from current plan if available
  const sttLimit = currentPlan?.stt_limit ?? null;
  const sttPercentage = getUsagePercentage(
    subscription.stt_units_used ?? 0,
    sttLimit
  );

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <div
        className="p-4 rounded-2xl border"
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('thirdLayer')
        }}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div
              className="h-12 w-12 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: getColour('secondLayer') }}
            >
              <Package className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3
                  className="text-sm font-semibold"
                  style={{ color: getColour('normalText') }}
                >
                  {subscription.plan_sku}
                </h3>
                <StatusBadge
                  status={subscription.is_active ? 'active' : 'inactive'}
                  label={subscription.is_active ? t('active') : t('inactive')}
                />
                <Badge color="blue">{subscription.billing_channel}</Badge>
              </div>
              <p
                className="text-sm mb-2"
                style={{ color: getColour('inactiveText') }}
              >
                {subscription.plan_description}
              </p>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <Calendar
                    className="h-3 w-3"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <span style={{ color: getColour('inactiveText') }}>
                    {t('billing-period')}:
                  </span>
                  <span style={{ color: getColour('normalText') }}>
                    {formatDate(subscription.start_date)} -{' '}
                    {formatDate(subscription.end_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedPlanId(subscription.plan_id);
              setChangePlanOpen(true);
            }}
            className="px-3 py-1.5 rounded-2xl text-xs font-medium bg-blue-500 text-white"
          >
            {t('change-plan')}
          </button>
        </div>
      </div>

      {/* Usage Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3
            className="text-sm font-semibold flex items-center gap-2"
            style={{ color: getColour('normalText') }}
          >
            <BarChart3 className="h-4 w-4" />
            {t('current-usage')}
          </h3>
          <button
            onClick={() => setResetConfirmOpen(true)}
            disabled={resetMutation.isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-xs font-medium disabled:opacity-50"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            <RefreshCw className="h-3 w-3" />
            {t('reset-usage')}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* STT Units */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('thirdLayer') }}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className="text-xs uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('stt-units')}
              </span>
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {(subscription.stt_units_used ?? 0).toLocaleString()}
                {sttLimit !== null ? (
                  <span style={{ color: getColour('inactiveText') }}>
                    {' '}
                    / {sttLimit.toLocaleString()}
                  </span>
                ) : (
                  <span
                    className="ml-1 inline-flex items-center gap-0.5"
                    style={{ color: getColour('inactiveText') }}
                  >
                    / <Infinity className="h-3 w-3" />
                  </span>
                )}
              </span>
            </div>
            {sttLimit !== null && (
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: getColour('secondLayer') }}
              >
                <div
                  className={`h-full rounded-full transition-all ${getUsageClass(sttPercentage)}`}
                  style={{ width: `${sttPercentage}%` }}
                />
              </div>
            )}
          </div>

          {/* Alignment Units */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('thirdLayer') }}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className="text-xs uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('alignment-units')}
              </span>
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {(subscription.alignment_units_used ?? 0).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Translation Characters */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('thirdLayer') }}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className="text-xs uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('translation-characters')}
              </span>
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {(
                  subscription.translation_characters_used ?? 0
                ).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Change Plan Modal */}
      {changePlanOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setChangePlanOpen(false)}
          />
          <div
            className="relative w-full max-w-md rounded-2xl shadow-xl p-6"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <button
              onClick={() => setChangePlanOpen(false)}
              className="absolute top-4 right-4 p-1 rounded-2xl hover:bg-opacity-80"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <X
                className="h-4 w-4"
                style={{ color: getColour('normalText') }}
              />
            </button>

            <h3
              className="text-lg font-semibold mb-4"
              style={{ color: getColour('normalText') }}
            >
              {t('change-plan')}
            </h3>

            <div className="mb-4">
              <label
                className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('select-new-plan')}
              </label>
              <div className="relative">
                <select
                  value={selectedPlanId ?? ''}
                  onChange={e => setSelectedPlanId(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-2xl text-sm appearance-none border"
                  style={{
                    backgroundColor: getColour('thirdLayer'),
                    color: getColour('normalText'),
                    borderColor: getColour('border')
                  }}
                >
                  {plans.map((plan: AdminPlan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.sku} - {plan.description}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
                  style={{ color: getColour('inactiveText') }}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setChangePlanOpen(false)}
                className="px-4 py-2 rounded-2xl text-sm font-medium"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  color: getColour('normalText')
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleChangePlan}
                disabled={
                  updateMutation.isLoading ||
                  selectedPlanId === subscription.plan_id
                }
                className="px-4 py-2 rounded-2xl text-sm font-medium bg-blue-500 text-white disabled:opacity-50"
              >
                {updateMutation.isLoading ? t('saving') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Usage Confirmation Modal */}
      {resetConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setResetConfirmOpen(false)}
          />
          <div
            className="relative w-full max-w-md rounded-2xl shadow-xl p-6"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <button
              onClick={() => setResetConfirmOpen(false)}
              className="absolute top-4 right-4 p-1 rounded-2xl hover:bg-opacity-80"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <X
                className="h-4 w-4"
                style={{ color: getColour('normalText') }}
              />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full flex items-center justify-center bg-amber-400/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <h3
                className="text-lg font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('reset-usage')}
              </h3>
            </div>

            <p
              className="text-sm mb-6"
              style={{ color: getColour('inactiveText') }}
            >
              {t('reset-usage-confirmation')}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setResetConfirmOpen(false)}
                className="px-4 py-2 rounded-2xl text-sm font-medium"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  color: getColour('normalText')
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleResetUsage}
                disabled={resetMutation.isLoading}
                className="px-4 py-2 rounded-2xl text-sm font-medium bg-amber-500 text-white disabled:opacity-50"
              >
                {resetMutation.isLoading ? t('resetting') : t('reset')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
